import {
  Deploy,
  FTEntryPointType,
  TransactorHashType,
} from '@block-explorer/entities';
import { MessageData } from './types';
import { guardedDeriveSplitDataFromArguments } from './utils';

const getCep18TransactorsDataFromArgs = (args) => {
  const recipientHash = guardedDeriveSplitDataFromArguments(
    args.recipient,
    'Hash'
  );
  const recipientAccountHash = guardedDeriveSplitDataFromArguments(
    args.recipient,
    'Account'
  );
  const ownerHash = guardedDeriveSplitDataFromArguments(args.owner, 'Account');
  const spenderHash = guardedDeriveSplitDataFromArguments(args.spender, 'Hash');

  return {
    recipientHash,
    recipientAccountHash,
    ownerHash,
    spenderHash,
  };
};

const prepareActionMessageData = (
  deploy: Deploy,
  getPublicKeyByAccountHash: (hash: string) => any
): MessageData | null => {
  const { entryPoint, args } = deploy;
  const entryPointName = entryPoint?.name || '';

  const actionName = {
    [FTEntryPointType.approve]: 'Approve transfer',
    [FTEntryPointType.mint]: 'Mint',
    [FTEntryPointType.burn]: 'Burn',
    [FTEntryPointType.transfer]: 'Transfer',
  };

  const getTokenSymbol = () => {
    if (
      entryPointName === 'mint' ||
      entryPointName === 'burn' ||
      entryPointName === 'transfer'
    ) {
      return 'tokens'; // contractPackage.metadata?.symbol;
    }

    return undefined;
  };

  const tokenSymbol = getTokenSymbol();

  if (entryPointName === FTEntryPointType.burn) {
    return {
      entryPointName: entryPointName,
      actionName: actionName[entryPointName] || entryPointName,
      ...(tokenSymbol && { symbol: tokenSymbol }),
    };
  }

  const { recipientHash, recipientAccountHash, ownerHash, spenderHash } =
    getCep18TransactorsDataFromArgs(args);

  if (
    actionName[entryPointName] === undefined ||
    !(recipientHash || recipientAccountHash || ownerHash || spenderHash)
  ) {
    return null;
  }

  const actionOutcome: Partial<MessageData> = {
    ...(tokenSymbol && { symbol: tokenSymbol }),
    // here could be a case when recipientAccountHash and recipientHash are both present
    // so, we need to avoid overwriting recipientAccountHash with recipientHash
    ...(recipientAccountHash?.hash
      ? {
          prefix1: 'to',
          account1: {
            accountHash: recipientAccountHash?.hash,
            publicKey:
              recipientAccountHash?.hash &&
              getPublicKeyByAccountHash(recipientAccountHash.hash),
          },
        }
      : {
          prefix1: 'to',
          account1: {
            hash: recipientHash?.hash,
          },
        }),
    ...(ownerHash?.hash && {
      prefix1: 'to',
      account1: {
        accountHash: ownerHash?.hash,
        publicKey: ownerHash?.hash && getPublicKeyByAccountHash(ownerHash.hash),
      },
    }),
    ...(spenderHash?.hash && {
      prefix1: 'to',
      account1: {
        hash: spenderHash?.hash,
      },
    }),
  };

  return {
    entryPointName: entryPointName,
    actionName: actionName[entryPointName] || entryPointName,
    ...actionOutcome,
  };
};

const prepareActionMessageDataForHome = (
  deploy: Deploy,
  getPublicKeyByAccountHash: (hash: string) => any
): MessageData | null => {
  const { entryPoint, callerPublicKey, callerHash } = deploy;
  const entryPointName = entryPoint?.name || '';

  const messageObject = prepareActionMessageData(
    deploy,
    getPublicKeyByAccountHash
  );
  const actionOutcome: Partial<MessageData> = {
    callerPrefix: entryPointName === 'transfer' ? 'from' : 'by',
    callerAccount: {
      publicKey: callerPublicKey || callerHash,
    },
  };

  if (messageObject === null) {
    return null;
  }

  return {
    ...messageObject,
    ...actionOutcome,
  };
};

const prepareActionMessageDataForDeployDetails = (
  deploy: Deploy,
  getPublicKeyByAccountHash: (hash: string) => any
): MessageData | null => {
  const { entryPoint, args, contractPackage } = deploy;
  const entryPointName = entryPoint?.name || '';
  const amount = deploy.args.amount?.parsed as string;

  const actionName = {
    [FTEntryPointType.transfer]: 'Transfer',
    [FTEntryPointType.approve]: 'Approve transfer rights',
    [FTEntryPointType.burn]: 'Burn',
    [FTEntryPointType.mint]: 'Mint',
  };

  const contractPrefixesMap = {
    [FTEntryPointType.transfer]: 'of',
    [FTEntryPointType.burn]: 'of',
    [FTEntryPointType.mint]: 'of',
    [FTEntryPointType.approve]: 'for',
  };

  const senderPrefixesMap = {
    [FTEntryPointType.transfer]: 'from',
    [FTEntryPointType.burn]: 'owned by',
    [FTEntryPointType.mint]: 'to',
    [FTEntryPointType.approve]: 'to',
  };

  const { recipientHash, recipientAccountHash, ownerHash, spenderHash } =
    getCep18TransactorsDataFromArgs(args);

  if (
    actionName[entryPointName] === undefined ||
    !amount ||
    !(recipientHash || recipientAccountHash || ownerHash || spenderHash)
  ) {
    return null;
  }

  const tokenSymbol = contractPackage.metadata?.symbol;
  const senderPrefix = senderPrefixesMap[entryPointName];
  const contractPrefix = contractPrefixesMap[entryPointName];

  const actionOutcome: Partial<MessageData> = {
    amount,
    ...(tokenSymbol && { symbol: tokenSymbol }),
    ...(contractPrefix && { contractPrefix }),
    ...(ownerHash?.hash && {
      prefix1: senderPrefix,
      account1: {
        hash: ownerHash?.hash,
        publicKey: ownerHash?.hash && getPublicKeyByAccountHash(ownerHash.hash),
        hashType: TransactorHashType.account,
      },
    }),
    ...(spenderHash?.hash && {
      prefix1: senderPrefix,
      account1: {
        hash: spenderHash?.hash,
        hashType: TransactorHashType.hash,
      },
    }),
    // here could be a case when recipientAccountHash and recipientHash are both present
    // so, we need to avoid overwriting recipientAccountHash with recipientHash
    ...(recipientAccountHash?.hash
      ? {
          prefix2: 'to',
          account2: {
            hash: recipientAccountHash?.hash,
            publicKey:
              recipientAccountHash?.hash &&
              getPublicKeyByAccountHash(recipientAccountHash.hash),
            hashType: TransactorHashType.account,
          },
        }
      : {
          prefix2: 'to',
          account2: {
            hash: recipientHash?.hash,
            hashType: TransactorHashType.hash,
          },
        }),
  };

  return {
    entryPointName: entryPointName,
    actionName: actionName[entryPointName] || entryPointName,
    ...actionOutcome,
  };
};

export const ftTokenMessage = () => {
  const getRelevantAccountHashesFromArgs = () => {
    return ['owner', 'spender', 'recipient'];
  };

  return {
    prepareActionMessageData,
    prepareActionMessageDataForHome,
    prepareActionMessageDataForDeployDetails,
    getRelevantAccountHashesFromArgs,
  };
};
